import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Download, ListChecks, MapPin, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../../utils/time';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, money, num, compact, fmtDate, plural,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, TotalsCheck, Notice,
  DateRange, RefreshButton, CHART,
} from '../financeKit';

const EMPTY = { shifts: [], by_client: [], total_km: 0, total_fuel: 0 };

// The report sends each shift's distance and fuel as `km` and `fuel_charge`. This page used to read
// `km_travelled` / `fuel_cost`, which the report has never sent, so every shift row showed 0.0 km
// and $0.00. The old names stay as a fallback.
const kmOf = (r) => r.km ?? r.km_travelled;
const fuelOf = (r) => r.fuel_charge ?? r.fuel_cost;

const fmtKm = (v) => `${num(v, 1)} km`;
const day = (v) => fmtDate(v ? String(v).slice(0, 10) : '');
const firstWord = (v) => String(v || '—').split(' ')[0];

function downloadCSV(filename, headers, rows) {
  const lines = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Chart tooltip: the client's full name, distance and fuel, in the same style as the money tooltips.
function ClientTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload || {};
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-card-deep">
      <p className="mb-1.5 font-semibold text-slate-700">{d.name}</p>
      <p className="flex items-center justify-between gap-6">
        <span className="text-slate-600">Distance</span>
        <span className="font-semibold tabular-nums text-slate-900">{fmtKm(d.km)}</span>
      </p>
      <p className="flex items-center justify-between gap-6">
        <span className="text-slate-600">Fuel reimbursement</span>
        <span className="font-semibold tabular-nums text-slate-900">{money(d.fuel)}</span>
      </p>
    </div>
  );
}

export default function KMReport() {
  const [data, setData]   = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date();
  const [from, setFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/km?from=${from}&to=${to}`);
      setData(r.data || EMPTY);
    } catch (e) {
      setData(EMPTY);
      setError(e.response?.data?.message || "We couldn't load the km report. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const shifts = data.shifts || [];
  const byClient = data.by_client || [];

  function exportCSV() {
    const headers = ['Date','Staff','Client','KM','Fuel $'];
    const rows = shifts.map(row => [
      row.shift_date?.slice(0, 10) || '', row.staff_name || '', row.client_name || '',
      Number(kmOf(row) || 0).toFixed(1), Number(fuelOf(row) || 0).toFixed(2),
    ]);
    downloadCSV(`km-travel-${from}-to-${to}.csv`, headers, rows);
    toast.success(`Exported ${plural(shifts.length, 'shift')}`);
  }

  const chartData = byClient.map(c => ({
    name: c.client_name || '—',
    km: parseFloat(c.total_km || 0),
    fuel: parseFloat(c.total_fuel || 0),
  }));

  // Totals rows are the plain sum of the rows shown.
  const clientShifts = byClient.reduce((s, r) => s + (parseInt(r.shifts, 10) || 0), 0);
  const clientKm     = byClient.reduce((s, r) => s + (parseFloat(r.total_km) || 0), 0);
  const clientFuel   = byClient.reduce((s, r) => s + (parseFloat(r.total_fuel) || 0), 0);
  const detailKm     = shifts.reduce((s, r) => s + (Number(kmOf(r)) || 0), 0);
  const detailFuel   = shifts.reduce((s, r) => s + (Number(fuelOf(r)) || 0), 0);

  // One row per shift, same filter as the summary: the detail totals must equal the tiles.
  const kmDiffers = !loading && Math.round(detailKm * 10) !== Math.round((Number(data.total_km) || 0) * 10);

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="KM and travel"
        description="Kilometres and fuel reimbursement on approved shifts with km recorded."
        actions={(
          <>
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="km" />
            <RefreshButton onClick={load} loading={loading} label="Refresh report" />
            <button type="button" onClick={exportCSV} disabled={shifts.length === 0} className="btn-secondary">
              <Download size={14} /> Export CSV
            </button>
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Kilometres" value={loading ? '…' : fmtKm(data.total_km)} accent="slate"
              sub={loading ? null : 'Travel logged'} />
            <KpiTile label="Fuel reimbursement" value={loading ? '…' : money(data.total_fuel)} accent="primary"
              sub={loading ? null : 'Paid to staff'} />
            <KpiTile label="Shifts with km" value={loading ? '…' : num(shifts.length)} accent="slate"
              sub={loading ? null : 'In this range'} />
            <KpiTile label="Clients with travel" value={loading ? '…' : num(byClient.length)} accent="slate" />
          </div>

          {/* By-client chart */}
          {!loading && chartData.length > 0 && (
            <SectionCard title="Kilometres by client" icon={BarChart3} bodyClass="p-4">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: CHART.axis }} tickFormatter={firstWord} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: CHART.axis }} tickFormatter={(v) => `${compact(v)} km`} width={56} tickLine={false} axisLine={false} />
                  <Tooltip content={<ClientTooltip />} cursor={{ fill: 'rgba(148,163,184,.12)' }} />
                  <Bar dataKey="km" name="Kilometres" fill={CHART.revenue} radius={[3, 3, 0, 0]} maxBarSize={36} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>
          )}

          {/* By-client summary */}
          {!loading && byClient.length > 0 && (
            <SectionCard title={`Summary by client · ${byClient.length}`} icon={Users}>
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Client</th>
                      <th scope="col" className={TH_NUM}>Shifts with km</th>
                      <th scope="col" className={TH_NUM}>Kilometres</th>
                      <th scope="col" className={TH_NUM}>Fuel reimbursement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byClient.map((row, i) => (
                      <tr key={`${row.client_name || ''}-${i}`} className={TR}>
                        <td className={`${TD} font-medium text-slate-800`}>{row.client_name || '—'}</td>
                        <td className={TD_NUM}>{num(row.shifts)}</td>
                        <td className={TD_NUM}>{fmtKm(row.total_km)}</td>
                        <td className={TD_NUM}><MoneyCell value={row.total_fuel} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD}>Total</td>
                      <td className={TD_NUM}>{num(clientShifts)}</td>
                      <td className={TD_NUM}>{fmtKm(clientKm)}</td>
                      <td className={TD_NUM}><MoneyCell value={clientFuel} strong /></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="px-4 py-2.5 text-xs text-slate-500 border-t border-slate-100">
                A shift shared by several clients counts once for each of them, so this table can add up to more than the summary above.
              </p>
            </SectionCard>
          )}

          {/* Shift details — its totals row equals the tiles above. */}
          <SectionCard title={`Shift detail${loading ? '' : ` · ${shifts.length}`}`} icon={ListChecks}>
            {loading ? (
              <TableSkeleton rows={6} cols={5} />
            ) : shifts.length === 0 ? (
              <EmptyState icon={MapPin} title="No km recorded in this range"
                hint="Shifts appear here once they have km recorded and an approved timesheet. Pick a wider range, or check that workers are logging their trips." />
            ) : (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Date</th>
                      <th scope="col" className={TH}>Staff</th>
                      <th scope="col" className={TH}>Client</th>
                      <th scope="col" className={TH_NUM}>Kilometres</th>
                      <th scope="col" className={TH_NUM}>Fuel reimbursement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map((row, i) => (
                      <tr key={row.id || i} className={TR}>
                        <td className={`${TD} whitespace-nowrap`}>{day(row.shift_date)}</td>
                        <td className={`${TD} font-medium text-slate-800`}>{row.staff_name || '—'}</td>
                        <td className={TD}>{row.client_name || '—'}</td>
                        <td className={TD_NUM}>{fmtKm(kmOf(row))}</td>
                        <td className={TD_NUM}><MoneyCell value={fuelOf(row)} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD} colSpan={3}>Total</td>
                      <td className={TD_NUM}>{fmtKm(detailKm)}</td>
                      <td className={TD_NUM}><MoneyCell value={detailFuel} strong /></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>

          {!loading && (
            <>
              <TotalsCheck checks={[{ label: 'Fuel reimbursement', computed: detailFuel, expected: data.total_fuel }]} />
              {kmDiffers && (
                <Notice tone="amber" title="Kilometres differ from the summary">
                  Table {fmtKm(detailKm)}, summary {fmtKm(data.total_km)}.
                </Notice>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
